import type { DeviceLinkConfig } from "./deviceLink.types";
import { HUB_RETURN_URL_QUERY } from "./deviceLink.types";

function isNonEmptyString(v: unknown): v is string {
  return typeof v === "string" && v.trim().length > 0;
}

/** 从设备行解析结构化 link；兼容仅配置以 /link 开头的 legacy `url` */
export function getDeviceLinkConfig(
  configuration:
    | { link?: DeviceLinkConfig; url?: string | null }
    | null
    | undefined
): DeviceLinkConfig | null {
  const raw = configuration?.link;
  if (raw && isNonEmptyString(raw.vendor)) {
    return {
      vendor: raw.vendor.trim(),
      channel: (raw.channel || "web").trim(),
      action: (raw.action || "connect").trim(),
      queryMode: raw.queryMode ?? "auto",
      presetQuery: raw.presetQuery,
    };
  }
  const legacyUrl = configuration?.url;
  if (!isNonEmptyString(legacyUrl)) return null;
  const trimmed = legacyUrl.trim();
  const match = trimmed.match(/^\/?link\/([^/]+)\/([^/]+)\/([^/?#]+)/i);
  if (!match) return null;
  const hasQuery = trimmed.includes("?");
  return {
    vendor: match[1],
    channel: match[2],
    action: match[3],
    queryMode: hasQuery ? "none" : "auto",
  };
}

export function hasDeviceLinkConfig(
  configuration:
    | { link?: DeviceLinkConfig; url?: string | null }
    | null
    | undefined
): boolean {
  return getDeviceLinkConfig(configuration) != null;
}

/** Hub 连接页（设备卡片列表）默认返回地址 */
export function getHubConnectReturnUrl(origin?: string): string {
  const base =
    origin?.trim() ||
    (typeof window !== "undefined" ? window.location.origin : "");
  return `${base.replace(/\/$/, "")}/`;
}

export type BuildDeviceLinkUrlInput = {
  configuration: Record<string, unknown>;
  vendorId?: number;
  productId?: number;
  productName?: string;
  /** 写入 hubReturnUrl，供独立包「返回」按钮使用 */
  returnUrl?: string;
  origin?: string;
  linkPrefix?: string;
};

/**
 * 生成独立包跳转 URL。
 *
 * 默认打开 `/{vendor}/{channel}/?...`（与旧 Hub 及 Vite base 一致）。
 * 旧包资源与路由都以 `/{vendor}/web/` 为根；若打开 `/link/.../connect` 会白屏无报错。
 * Nginx 仍可将 `/link/.../connect` 302 到本路径，并映射 `/{vendor}/web/` → hub-link。
 */
export function buildDeviceLinkUrl(
  input: BuildDeviceLinkUrlInput
): string | null {
  const link = getDeviceLinkConfig(
    input.configuration as { link?: DeviceLinkConfig; url?: string | null }
  );
  if (!link) return null;

  // 兼容显式传入 linkPrefix（如测试）；默认不再走 /link/.../connect
  const customPrefix = input.linkPrefix?.trim();
  const path = customPrefix
    ? `${customPrefix.replace(/\/$/, "")}/${link.vendor}/${link.channel}/${link.action}`
    : `/${link.vendor}/${link.channel}/`;
  const origin =
    input.origin?.trim() ||
    (typeof window !== "undefined" ? window.location.origin : "");
  const base = origin ? `${origin.replace(/\/$/, "")}${path}` : path;

  const params = new URLSearchParams();
  const mode = link.queryMode ?? "auto";

  if (link.presetQuery) {
    for (const [k, v] of Object.entries(link.presetQuery)) {
      params.set(k, String(v));
    }
  }

  if (mode === "auto") {
    const vid = input.vendorId ?? input.configuration.vendorId;
    const pid = input.productId ?? input.configuration.productId;
    const pn = input.productName ?? input.configuration.productName ?? "";
    if (typeof pid === "number" && Number.isFinite(pid)) {
      params.set("productId", String(pid));
    }
    if (typeof vid === "number" && Number.isFinite(vid)) {
      params.set("vendorId", String(vid));
    }
    if (typeof pn === "string" && pn.trim()) {
      params.set("productName", pn.trim());
    }
  }

  const returnUrl = input.returnUrl ?? getHubConnectReturnUrl(input.origin);
  if (returnUrl) {
    params.set(HUB_RETURN_URL_QUERY, returnUrl);
  }

  const qs = params.toString();
  return qs ? `${base}?${qs}` : base;
}
